<script lang="ts">
	/**
	 * The "Your licences" block on the home screen — one card per licensing
	 * application, each showing the caller's seat usage and pointing into the
	 * licence list for that product.
	 *
	 * The whole block disappears when the account holds no licensing applications,
	 * so a user of a product that has no licensing backend sees nothing rather than
	 * an empty heading. A product whose backend is unreachable still gets a card,
	 * carrying the reason: knowing the licences could not be read is worth more
	 * than the tile silently vanishing.
	 */

	import { onMount } from 'svelte';
	import EmptyState from './EmptyState.svelte';
	import RoutedLink from './RoutedLink.svelte';
	import LicenseAppIcon from './LicenseAppIcon.svelte';
	import SeatMeter from './SeatMeter.svelte';
	import { listLicenseApplications } from '../AppNetwork';
	import site from '../site';
	import type { LicenseApplicationOverview } from '../AppTypes';
	import { t, t2 } from '../i18n';

	/** API origin the uploaded logos are served from. */
	const apiBase = site.backend.baseURL;

	/** The licensing applications and the caller's standing in each. */
	let overviews: LicenseApplicationOverview[] = $state([]);
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Failure of the listing itself, as opposed to one backend being down. */
	let error = $state('');

	/**
	 * Load the overviews. A failure here hides the block rather than shouting on the
	 * home screen — a licence panel that cannot load is not worth an error banner
	 * above the user's dashboard.
	 */
	async function load(): Promise<void> {
		try {
			const result = await listLicenseApplications();
			/* Guard the shape rather than trusting it: this block renders above
			   everything else on the home screen, so a malformed body must degrade
			   to "no licences" and not take the whole page down with it. */
			overviews = Array.isArray(result) ? result : [];
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load licenses');
		} finally {
			loading = false;
		}
	}

	onMount(load);

	/** Formats an expiry date, or a dash when the licence never lapses. */
	function formatExpiry(date: string | null): string {
		if (!date) return '—';
		return new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
	}
</script>

{#if loading}
	<div class="mt-6">
		<EmptyState variant="loading" compact />
	</div>
{:else if !error && overviews.length > 0}
	<div class="mt-6">
		<div class="mb-4 flex items-center gap-2">
			<span class="inline-block h-0.5 w-6 bg-red-500"></span>
			<span class="text-xs font-semibold tracking-widest text-red-500 uppercase dark:text-red-400">
				{$t('Your Licenses')}
			</span>
		</div>

		<div class="grid gap-4 sm:grid-cols-2">
			{#each overviews as overview (overview.applicationUUID)}
				<div class="widget group flex h-full flex-col p-5 text-left transition hover:shadow-md">
					<!-- The body navigates; the footer links are separate targets, so the
					     card cannot be one anchor wrapping the lot. -->
					<RoutedLink to="/Licenses/{overview.applicationUUID}" class="flex flex-1 flex-col">
						<!-- The wordmark gets the full width of the card and everything else
						     stacks beneath it: a logo is a masthead, and squeezing one into a
						     row beside the name and a chevron wastes the one thing it is good
						     at — being recognised at a glance. `dark:invert` follows the
						     navbar's treatment of the same asset. A product with no logo keeps
						     the square mark and its name, which is what that row was for. -->
						{#if overview.logoImageName}
							<img
								src="{apiBase}/uploads/applications/{overview.logoImageName}"
								alt={overview.name}
								class="h-10 w-full object-contain object-left dark:invert"
							/>
							{#if overview.summary}
								<p class="mt-3 text-xs text-gray-500 dark:text-gray-400">
									{$t('{{count}} license(s)', { count: overview.summary.licenseCount })}
								</p>
							{/if}
						{:else}
							<div class="flex items-center gap-3">
								<div class="bg-primary-100 dark:bg-primary-900/30 rounded-lg p-2">
									<LicenseAppIcon
										iconImageName={overview.iconImageName}
										icon={overview.icon}
										name={overview.name}
									/>
								</div>
								<div class="min-w-0 flex-1">
									<p class="truncate font-medium text-gray-900 dark:text-white">{overview.name}</p>
									{#if overview.summary}
										<p class="text-xs text-gray-500 dark:text-gray-400">
											{$t('{{count}} license(s)', { count: overview.summary.licenseCount })}
										</p>
									{/if}
								</div>
								<i
									class="fa-solid fa-chevron-right text-xs text-gray-300 transition group-hover:text-red-400 dark:text-gray-600"
								></i>
							</div>
						{/if}

						{#if overview.error}
							<p class="mt-4 text-xs text-yellow-600 dark:text-yellow-400">
								<i class="fa-solid fa-triangle-exclamation me-1"></i>{overview.error}
							</p>
						{:else if overview.summary}
							<!-- Seats across the active licences only — see `summarise` on the
							     server. A lapsed licence grants no seat to activate against. -->
							<SeatMeter
								class="mt-auto pt-4"
								used={overview.summary.seatsUsed}
								total={overview.summary.seatsTotal}
							/>

							<div class="mt-3 flex items-center justify-between text-xs">
								<span class="text-gray-500 dark:text-gray-400">
									{$t('{{count}} active', { count: overview.summary.activeLicenseCount })}
									<!-- Named rather than merely excluded from the bar: a licence you
									     still hold but cannot use is worth knowing about, and the
									     seat figures above silently ignoring it is not an answer. -->
									{#if overview.summary.expiredLicenseCount > 0}
										<span class="text-yellow-600 dark:text-yellow-400">
											· {$t('{{count}} expired', { count: overview.summary.expiredLicenseCount })}
										</span>
									{/if}
								</span>
								<span class="text-gray-500 dark:text-gray-400">
									{$t('Renews')}
									{formatExpiry(overview.summary.nextExpiry)}
								</span>
							</div>
						{/if}
					</RoutedLink>

					<!-- Jumps straight to a section of the product's page — each is a route
					     of its own. Documentation is still dropped here when the product
					     publishes none: on the page it is a shelf that happens to be empty,
					     but on the card it would be a link promising something to read. -->
					<div
						class="mt-4 flex flex-wrap items-center gap-4 border-t border-gray-200 pt-3 text-xs dark:border-gray-700"
					>
						<RoutedLink to="/Downloads/{overview.applicationUUID}">
							<span class="text-primary-600 dark:text-primary-400 hover:underline">
								<i class="fa-solid fa-download me-1"></i>{$t('Downloads')}
							</span>
						</RoutedLink>
						<RoutedLink to="/Licenses/{overview.applicationUUID}">
							<span class="text-primary-600 dark:text-primary-400 hover:underline">
								<i class="fa-solid fa-key me-1"></i>{$t('Licenses')}
							</span>
						</RoutedLink>
						{#if overview.documentationLinks.length > 0}
							<RoutedLink to="/Documentation/{overview.applicationUUID}">
								<span class="text-primary-600 dark:text-primary-400 hover:underline">
									<i class="fa-solid fa-book me-1"></i>{$t('Documentation')}
								</span>
							</RoutedLink>
						{/if}
						<!-- Holding nothing is the one case where the useful next step is off
						     this site entirely, so the offer sits with the section links. -->
						{#if overview.summary?.licenseCount === 0 && overview.licensePurchaseURL}
							<a
								href={overview.licensePurchaseURL}
								target="_blank"
								rel="noopener noreferrer"
								class="text-primary-600 dark:text-primary-400 ms-auto hover:underline"
							>
								<i class="fa-solid fa-cart-shopping me-1"></i>{$t('Get a license')}
							</a>
						{/if}
					</div>
				</div>
			{/each}
		</div>
	</div>
{/if}
